import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PublicIcon from '@mui/icons-material/Public';
import SearchIcon from '@mui/icons-material/Search';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import type { FieldOption, FormField, Rule } from '@re/form-engine';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  InputAdornment,
  ListItemIcon,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { Button } from '../../components/button';
import { TextField } from '../../components/text-field';
import { isEn, tr } from '../../lib/i18n';
import { matches, normalize } from '../../lib/search';
import { COUNTRIES } from './countries';
import { toKey } from './helpers';

type Opt = FieldOption;
const PAGE = 60;

/** CODE from English text: "Sea view" → SEA_VIEW */
const codeOf = (en: string) => toKey(en).toUpperCase();

/** Give every option a unique code (adds _2, _3 … to repeats). */
function uniqueCodes(opts: Opt[]): Opt[] {
  const seen = new Set<string>();
  return opts.map((o, i) => {
    let base = (o.value || `OPTION_${i + 1}`).toUpperCase().replace(/[^A-Z0-9_]/g, '_');
    if (!base.replace(/_/g, '')) base = `OPTION_${i + 1}`;
    let v = base;
    for (let n = 2; seen.has(v); n++) v = `${base}_${n}`;
    seen.add(v);
    return v === o.value ? o : { ...o, value: v };
  });
}

/** Who an option is offered to: the request types of an earlier «نوع الطلب» field. */
export interface Audience {
  key: string;
  types: { code: string; label: string }[];
}

/** [] = every request type; null = a hand-written rule the picker can't show. */
function audienceCodes(rule: Rule | null | undefined, key: string): string[] | null {
  if (!rule) return [];
  const c = rule as { field?: string; op?: string; value?: unknown };
  if (c.field === key && (c.op === 'in' || c.op === 'eq')) return Array.isArray(c.value) ? (c.value as string[]) : [String(c.value)];
  return null;
}

function OptionAudience({ rule, audience, onChange }: { rule: Rule | null | undefined; audience: Audience; onChange: (r: Rule | null) => void }) {
  const codes = audienceCodes(rule, audience.key);
  const name = (c: string) => audience.types.find((t) => t.code === c)?.label ?? c;
  if (codes === null) {
    return (
      <Tooltip title={tr('شرط مخصص على هذا الخيار — امسحه لتختار الأنواع', 'A custom rule on this option — clear it to pick types')}>
        <Button size="small" variant="outlined" color="warning" sx={{ width: 150, flexShrink: 0 }} onClick={() => onChange(null)}>{tr('شرط مخصص ✕', 'Custom rule ✕')}</Button>
      </Tooltip>
    );
  }
  return (
    <FormControl size="small" sx={{ width: 150, flexShrink: 0 }}>
      <Select
        multiple
        displayEmpty
        value={codes}
        onChange={(e) => {
          const v = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
          onChange(v.length && v.length < audience.types.length ? { field: audience.key, op: 'in', value: v } : null);
        }}
        renderValue={(v) => (
          <Typography variant="body2" noWrap color={v.length ? 'primary' : 'text.secondary'}>
            {v.length ? (v.length <= 2 ? v.map(name).join('، ') : tr(`${v.length} أنواع`, `${v.length} types`)) : tr('كل الأنواع', 'All types')}
          </Typography>
        )}
        inputProps={{ 'aria-label': tr('يظهر لأنواع الطلب', 'Shown for request types') }}
      >
        {audience.types.map((t) => (
          <MenuItem key={t.code} value={t.code} dense>{t.label}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export function OptionsEditor({ field, onChange, audience }: { field: FormField; onChange: (o: Opt[]) => void; audience?: Audience }) {
  const opts = field.config.options ?? [];
  const [q, setQ] = useState('');
  const [limit, setLimit] = useState(PAGE);
  const [bulk, setBulk] = useState(false);
  const [range, setRange] = useState(false);
  const [preset, setPreset] = useState<null | { title: string; options: Opt[] }>(null);
  const [menu, setMenu] = useState<HTMLElement | null>(null);

  const setAt = (i: number, patch: Partial<Opt>) => onChange(opts.map((o, j) => (j === i ? { ...o, ...patch } : o)));
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= opts.length) return;
    const next = [...opts];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  };
  const shown = useMemo(() => opts.map((o, i) => ({ o, i })).filter(({ o }) => matches(q, o.label.ar, o.label.en, o.value)), [opts, q]);
  const missingEn = opts.filter((o) => o.label.ar?.trim() && !o.label.en?.trim()).length;
  const dupes = opts.length - new Set(opts.map((o) => normalize(o.label.ar))).size;

  const sortAZ = () => {
    const lang = isEn() ? 'en' : 'ar';
    onChange([...opts].sort((a, b) => (a.label[lang] || a.label.ar || '').localeCompare(b.label[lang] || b.label.ar || '', lang)));
  };
  const dedupe = () => {
    const seen = new Set<string>();
    onChange(opts.filter((o) => {
      const k = normalize(o.label.ar) || o.value;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    }));
  };
  const add = () => {
    const n = opts.length + 1;
    onChange(uniqueCodes([...opts, { value: `OPTION_${n}`, label: { ar: '', en: '' } }]));
    setQ('');
    setLimit(Math.max(limit, n));
  };

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={add}>{tr('خيار', 'Option')}</Button>
        <Button size="small" variant="outlined" startIcon={<EditNoteIcon />} onClick={() => setBulk(true)}>{tr('تعديل جماعي / لصق', 'Bulk edit / paste')}</Button>
        <Button size="small" variant="outlined" startIcon={<AutoFixHighIcon />} onClick={(e) => setMenu(e.currentTarget)}>{tr('قوالب جاهزة', 'Presets')}</Button>
        <Box sx={{ flex: 1 }} />
        <Tooltip title={tr('ترتيب أبجدي', 'Sort A–Z')}><span><IconButton size="small" onClick={sortAZ} disabled={opts.length < 2}><SortByAlphaIcon fontSize="small" /></IconButton></span></Tooltip>
      </Stack>
      <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)}>
        <MenuItem onClick={() => { setMenu(null); setPreset({ title: tr('الدول', 'Countries'), options: COUNTRIES.map(([v, ar, en]) => ({ value: v, label: { ar, en } })) }); }}>
          <ListItemIcon><PublicIcon fontSize="small" /></ListItemIcon>{tr(`الدول (${COUNTRIES.length})`, `Countries (${COUNTRIES.length})`)}
        </MenuItem>
        <MenuItem onClick={() => { setMenu(null); setRange(true); }}>
          <ListItemIcon><FormatListNumberedIcon fontSize="small" /></ListItemIcon>{tr('أرقام متسلسلة (مثل 1 – 100 سنة)', 'Number series (e.g. 1 – 100 years)')}
        </MenuItem>
        <MenuItem onClick={() => { setMenu(null); setPreset({ title: tr('نعم / لا', 'Yes / No'), options: [{ value: 'YES', label: { ar: 'نعم', en: 'Yes' } }, { value: 'NO', label: { ar: 'لا', en: 'No' } }] }); }}>
          <ListItemIcon><AutoFixHighIcon fontSize="small" /></ListItemIcon>{tr('نعم / لا', 'Yes / No')}
        </MenuItem>
      </Menu>

      {(missingEn > 0 || dupes > 0) && (
        <Alert severity="warning" sx={{ py: 0 }} action={dupes > 0 ? <Button color="inherit" size="small" onClick={dedupe}>{tr('إزالة المكرر', 'Remove duplicates')}</Button> : undefined}>
          {[missingEn > 0 && tr(`${missingEn} خيار بدون نص إنجليزي`, `${missingEn} option(s) without English`), dupes > 0 && tr(`${dupes} مكرر`, `${dupes} duplicate(s)`)].filter(Boolean).join(' · ')}
        </Alert>
      )}

      {opts.length > 8 && (
        <TextField size="small" placeholder={tr(`ابحث في ${opts.length} خيار…`, `Search ${opts.length} options…`)} value={q} onChange={(e) => { setQ(e.target.value); setLimit(PAGE); }}
          slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }} />
      )}

      <Typography variant="caption" color="text.secondary">{tr('الكود هو ما يُحفظ وما تعتمد عليه الشروط — يُولَّد من النص الإنجليزي، ولا تغيّره بعد النشر.', 'The code is what gets stored and what conditions use — it follows the English text; don’t change it after publishing.')}</Typography>
      {audience && (
        <Typography variant="caption" color="text.secondary">{tr('«يظهر لـ»: اختر أنواع الطلب التي يظهر لها الخيار. فارغ = لكل الأنواع. إذا لم يبقَ أي خيار لنوع طلب، يختفي الحقل كله عنده.', '“Shown for”: pick the request types that get this option. Empty = all types. If a request type is left with no option, the whole field is hidden for it.')}</Typography>
      )}

      <Stack spacing={0.75}>
        {shown.slice(0, limit).map(({ o, i }) => (
          <Stack key={i} direction="row" spacing={0.75} alignItems="center">
            <Typography variant="caption" color="text.secondary" sx={{ width: 26, textAlign: 'center', flexShrink: 0 }}>{i + 1}</Typography>
            <TextField size="small" placeholder="عربي" value={o.label.ar ?? ''} sx={{ flex: 3 }} error={!o.label.ar?.trim()}
              onChange={(e) => setAt(i, { label: { ...o.label, ar: e.target.value } })} slotProps={{ htmlInput: { dir: 'rtl', 'aria-label': tr('النص العربي', 'Arabic text') } }} />
            <TextField size="small" placeholder="English" value={o.label.en ?? ''} sx={{ flex: 3 }}
              onChange={(e) => {
                const en = e.target.value;
                const prev = o.label.en ? codeOf(o.label.en) : '';
                const following = /^OPTION_\d+$/.test(o.value) || (!!prev && o.value === prev);
                const auto = following && en ? codeOf(en) : '';
                const value = auto && !opts.some((x, j) => j !== i && x.value === auto) ? auto : o.value;
                setAt(i, { value, label: { ...o.label, en } });
              }}
              slotProps={{ htmlInput: { dir: 'ltr', 'aria-label': tr('النص الإنجليزي', 'English text') } }} />
            <TextField size="small" value={o.value} sx={{ flex: 2, '& input': { fontFamily: 'monospace', fontSize: 12 } }}
              error={opts.some((x, j) => j !== i && x.value === o.value)}
              onChange={(e) => setAt(i, { value: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_') })} slotProps={{ htmlInput: { dir: 'ltr', 'aria-label': tr('الكود', 'Code') } }} />
            {audience && <OptionAudience rule={o.visibleWhen} audience={audience} onChange={(r) => setAt(i, { visibleWhen: r })} />}
            <Stack direction="row" sx={{ flexShrink: 0 }}>
              <IconButton size="small" onClick={() => move(i, -1)} disabled={i === 0 || !!q} aria-label={tr('للأعلى', 'Move up')}><ArrowUpwardIcon sx={{ fontSize: 16 }} /></IconButton>
              <IconButton size="small" onClick={() => move(i, 1)} disabled={i === opts.length - 1 || !!q} aria-label={tr('للأسفل', 'Move down')}><ArrowDownwardIcon sx={{ fontSize: 16 }} /></IconButton>
              <IconButton size="small" onClick={() => onChange(opts.filter((_, j) => j !== i))} aria-label={tr('حذف الخيار', 'Delete option')}><DeleteOutlineIcon sx={{ fontSize: 18 }} /></IconButton>
            </Stack>
          </Stack>
        ))}
      </Stack>
      {shown.length > limit && (
        <Button size="small" onClick={() => setLimit(limit + PAGE * 2)} sx={{ alignSelf: 'center' }}>
          {tr(`إظهار المزيد (${shown.length - limit} متبقٍ)`, `Show more (${shown.length - limit} left)`)}
        </Button>
      )}
      {q && !shown.length && <Typography variant="body2" color="text.secondary">{tr('لا نتائج', 'No results')}</Typography>}

      {bulk && <BulkDialog options={opts} onClose={() => setBulk(false)} onApply={(o) => { onChange(o); setBulk(false); }} />}
      {range && <RangeDialog onClose={() => setRange(false)} onPick={(o) => { setRange(false); setPreset({ title: tr('أرقام متسلسلة', 'Number series'), options: o }); }} />}
      {preset && (
        <ApplyDialog title={preset.title} incoming={preset.options} existing={opts} onClose={() => setPreset(null)}
          onApply={(o) => { onChange(o); setPreset(null); setQ(''); }} />
      )}
    </Stack>
  );
}

/** Lines "عربي | English | CODE" (tabs from a spreadsheet work too). */
export function parseLines(text: string): Opt[] {
  const out = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line, i) => {
      const [ar = '', en = '', code = ''] = line.split(/\s*[|\t]\s*/);
      const value = (code || (en ? codeOf(en) : '') || `OPTION_${i + 1}`).toUpperCase().replace(/[^A-Z0-9_]/g, '_');
      return { value, label: en.trim() ? { ar: ar.trim(), en: en.trim() } : { ar: ar.trim() } };
    });
  return uniqueCodes(out);
}
const toLines = (opts: Opt[]) => opts.map((o) => [o.label.ar ?? '', o.label.en ?? '', o.value].join(' | ')).join('\n');

function BulkDialog({ options, onClose, onApply }: { options: Opt[]; onClose: () => void; onApply: (o: Opt[]) => void }) {
  const [text, setText] = useState(() => toLines(options));
  const parsed = useMemo(() => parseLines(text), [text]);
  const noEn = parsed.filter((o) => !o.label.en).length;
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{tr('تعديل الخيارات دفعة واحدة', 'Edit all options at once')}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {tr('سطر لكل خيار: ', 'One line per option: ')}<Box component="code" dir="ltr" sx={{ px: 0.5, bgcolor: 'action.hover', borderRadius: 1 }}>عربي | English | CODE</Box>
          {tr(' — الإنجليزي والكود اختياريان (الكود يُولَّد من الإنجليزي). يمكنك اللصق من Excel مباشرة.', ' — English and code are optional (the code is made from the English). You can paste straight from Excel.')}
        </Typography>
        <TextField multiline minRows={12} maxRows={22} fullWidth value={text} onChange={(e) => setText(e.target.value)}
          slotProps={{ htmlInput: { dir: 'auto', spellCheck: false, style: { fontFamily: 'monospace', fontSize: 13, lineHeight: 1.7 } } }} />
        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
          <Typography variant="caption">{tr(`${parsed.length} خيار`, `${parsed.length} option(s)`)}</Typography>
          {noEn > 0 && <Typography variant="caption" color="warning.main">{tr(`${noEn} بدون إنجليزي`, `${noEn} without English`)}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{tr('إلغاء', 'Cancel')}</Button>
        <Button variant="contained" onClick={() => onApply(parsed)}>{tr('تطبيق', 'Apply')}</Button>
      </DialogActions>
    </Dialog>
  );
}

const UNIT_PRESETS = [
  { id: 'years', prefix: 'YEARS', ar1: 'سنة', ar2: 'سنتين', arN: 'سنوات', en1: 'year', enN: 'years' },
  { id: 'rooms', prefix: 'ROOMS', ar1: 'غرفة', ar2: 'غرفتين', arN: 'غرف', en1: 'room', enN: 'rooms' },
  { id: 'baths', prefix: 'BATHS', ar1: 'حمام', ar2: 'حمامين', arN: 'حمامات', en1: 'bathroom', enN: 'bathrooms' },
  { id: 'floors', prefix: 'FLOORS', ar1: 'طابق', ar2: 'طابقين', arN: 'طوابق', en1: 'floor', enN: 'floors' },
  { id: 'none', prefix: 'N', ar1: '', ar2: '', arN: '', en1: '', enN: '' },
];

/** 1 → "سنة", 2 → "سنتين", 3–10 → "3 سنوات", 11+ → "11 سنة" */
function arCount(n: number, u: { ar1: string; ar2: string; arN: string }) {
  if (!u.ar1) return String(n);
  if (n === 1) return u.ar1;
  if (n === 2) return u.ar2 || `2 ${u.arN}`;
  if (n >= 3 && n <= 10) return `${n} ${u.arN}`;
  return `${n} ${u.ar1}`;
}

function RangeDialog({ onClose, onPick }: { onClose: () => void; onPick: (o: Opt[]) => void }) {
  const [from, setFrom] = useState(1);
  const [to, setTo] = useState(100);
  const [step, setStep] = useState(1);
  const [u, setU] = useState(UNIT_PRESETS[0]!);
  const list = useMemo(() => {
    const out: Opt[] = [];
    if (step <= 0 || to < from) return out;
    for (let n = from; n <= to && out.length < 1000; n += step) {
      out.push({ value: `${u.prefix || 'N'}_${n}`, label: { ar: arCount(n, u), en: u.en1 ? `${n} ${n === 1 ? u.en1 : u.enN}` : String(n) } });
    }
    return out;
  }, [from, to, step, u]);
  const num = (s: string) => (s === '' ? 0 : Number(s));
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{tr('أرقام متسلسلة', 'Number series')}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={1.5}>
          <Grid size={4}><TextField fullWidth size="small" type="number" label={tr('من', 'From')} value={from} onChange={(e) => setFrom(num(e.target.value))} /></Grid>
          <Grid size={4}><TextField fullWidth size="small" type="number" label={tr('إلى', 'To')} value={to} onChange={(e) => setTo(num(e.target.value))} /></Grid>
          <Grid size={4}><TextField fullWidth size="small" type="number" label={tr('الخطوة', 'Step')} value={step} onChange={(e) => setStep(num(e.target.value))} /></Grid>
          <Grid size={12}>
            <TextField select fullWidth size="small" label={tr('الوحدة', 'Unit')} value={u.id} onChange={(e) => setU(UNIT_PRESETS.find((x) => x.id === e.target.value)!)}>
              {UNIT_PRESETS.map((p) => <MenuItem key={p.id} value={p.id}>{p.id === 'none' ? tr('بدون وحدة', 'No unit') : `${p.arN} / ${p.enN}`}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={6}><TextField fullWidth size="small" label={tr('مفرد', 'Singular')} value={u.ar1} onChange={(e) => setU({ ...u, id: 'custom', ar1: e.target.value })} slotProps={{ htmlInput: { dir: 'rtl' } }} /></Grid>
          <Grid size={6}><TextField fullWidth size="small" label={tr('مثنى', 'Dual')} value={u.ar2} onChange={(e) => setU({ ...u, id: 'custom', ar2: e.target.value })} slotProps={{ htmlInput: { dir: 'rtl' } }} /></Grid>
          <Grid size={6}><TextField fullWidth size="small" label={tr('جمع (3–10)', 'Plural (3–10)')} value={u.arN} onChange={(e) => setU({ ...u, id: 'custom', arN: e.target.value })} slotProps={{ htmlInput: { dir: 'rtl' } }} /></Grid>
          <Grid size={6}><TextField fullWidth size="small" label={tr('بادئة الكود', 'Code prefix')} value={u.prefix} onChange={(e) => setU({ ...u, id: 'custom', prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_') })} slotProps={{ htmlInput: { dir: 'ltr' } }} /></Grid>
          <Grid size={6}><TextField fullWidth size="small" label="English (1)" value={u.en1} onChange={(e) => setU({ ...u, id: 'custom', en1: e.target.value })} slotProps={{ htmlInput: { dir: 'ltr' } }} /></Grid>
          <Grid size={6}><TextField fullWidth size="small" label="English (2+)" value={u.enN} onChange={(e) => setU({ ...u, id: 'custom', enN: e.target.value })} slotProps={{ htmlInput: { dir: 'ltr' } }} /></Grid>
        </Grid>
        <Divider sx={{ my: 1.5 }} />
        <Typography variant="caption" color="text.secondary">{tr(`${list.length} خيار — أمثلة:`, `${list.length} options — e.g.:`)}</Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>{[...list.slice(0, 4), ...list.slice(10, 12)].map((o) => o.label.ar).join('، ')}…</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{tr('إلغاء', 'Cancel')}</Button>
        <Button variant="contained" disabled={!list.length} onClick={() => onPick(list)}>{tr('متابعة', 'Continue')}</Button>
      </DialogActions>
    </Dialog>
  );
}

function ApplyDialog({ title, incoming, existing, onClose, onApply }: { title: string; incoming: Opt[]; existing: Opt[]; onClose: () => void; onApply: (o: Opt[]) => void }) {
  const placeholder = existing.length === 0 || (existing.length === 1 && /^OPTION_\d+$/.test(existing[0]!.value));
  const [mode, setMode] = useState<'replace' | 'append'>('replace');
  const apply = () => onApply(placeholder || mode === 'replace' ? incoming : uniqueCodes([...existing, ...incoming.filter((o) => !existing.some((x) => x.value === o.value))]));
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2">{tr(`سيُضاف ${incoming.length} خيار بالعربي والإنجليزي.`, `${incoming.length} options will be added in Arabic and English.`)}</Typography>
        {!placeholder && (
          <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as 'replace' | 'append')} sx={{ mt: 1 }}>
            <FormControlLabel value="replace" control={<Radio size="small" />} label={tr(`استبدال الخيارات الحالية (${existing.length})`, `Replace the current options (${existing.length})`)} />
            <FormControlLabel value="append" control={<Radio size="small" />} label={tr('إضافتها بعد الحالية', 'Add after the current ones')} />
          </RadioGroup>
        )}
        {!placeholder && mode === 'replace' && <Alert severity="warning" sx={{ mt: 1 }}>{tr('إذا كانت شروط تعتمد على أكواد قديمة فراجعها بعد الاستبدال.', 'If conditions use the old codes, review them after replacing.')}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{tr('إلغاء', 'Cancel')}</Button>
        <Button variant="contained" onClick={apply}>{tr('تطبيق', 'Apply')}</Button>
      </DialogActions>
    </Dialog>
  );
}
